import React from "react";

/**
 * PageHeader — consistent header across output pages.
 * Layout: eyebrow (mono caps) → big title → optional subtitle → meta row + actions on the right.
 *
 *   <PageHeader
 *     eyebrow="JD Analysis"
 *     title="Senior Backend Engineer"
 *     subtitle="Acme Corp · San Francisco"
 *     meta={[{ label: "Fit Score", value: "78/100", tone: "good" }]}
 *     actions={<button>Re-tailor</button>}
 *   />
 *
 * Tone values map to colour: success / good / warn / danger / neutral
 */
const TONE_COLOR = {
  success: "#1A7F5C",
  good: "#20C1A7",
  warn: "#E8AF31",
  danger: "#DC2626",
  neutral: "#475569",
};

export default function PageHeader({
  eyebrow,
  title,
  subtitle,
  meta = [],
  actions,
  testid = "page-header",
}) {
  return (
    <header className="mb-5 md:mb-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          {eyebrow && (
            <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1.5"
                 style={{ color: "#B91C1C", fontWeight: 700 }}>
              · {eyebrow}
            </div>
          )}
          {title && (
            <h1 className="font-display leading-tight"
                style={{
                  fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
                  color: "#0F1B2D",
                  letterSpacing: "-0.015em",
                  fontWeight: 700,
                }}
                data-testid={`${testid}-title`}>
              {title}
            </h1>
          )}
          {subtitle && (
            <div className="text-[14px] mt-1.5" style={{ color: "#475569", fontWeight: 500 }}
                 data-testid={`${testid}-subtitle`}>
              {subtitle}
            </div>
          )}
          {meta.length > 0 && (
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1.5 mt-3"
                 data-testid={`${testid}-meta`}>
              {meta.map((m, i) => {
                const color = TONE_COLOR[m.tone] || TONE_COLOR.neutral;
                return (
                  <div key={i} className="flex items-center gap-1.5">
                    <span className="font-mono-rm uppercase text-[10px] tracking-widest"
                          style={{ color: "#64748B", fontWeight: 600 }}>
                      {m.label}
                    </span>
                    <span className="text-[13px] font-semibold"
                          style={{ color }}>
                      {m.value}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0" data-testid={`${testid}-actions`}>
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}
